<template>
  <div class="sp-input-range-slider">
    <div class="sp-input-range-slider__track-container">
      <div class="sp-input-range-slider__track-background" />
      <div class="sp-input-range-slider__track-fill" :style="fillWidth" />
      <div v-if="showTicks" id="markers" class="sp-input-range-slider__ticks">
        <div v-for="tick in tickCount" :key="tick" class="sp-input-range-slider__tick"></div>
      </div>
      <input
        class="sp-input-range-slider__input"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        :value="model"
        :disabled="disabled"
        @input.stop.prevent="onInput"
        @change.stop="onChange"
      />
    </div>
    <slot name="append" />
  </div>
</template>

<script setup>
import { props as rangeProps } from "./props.js";
import { computed, ref, toRef, useAttrs, watch } from "vue";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { toBoolean } from "#ce/utils/props";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-input-range-slider is a genuine form control: submit a value, match :disabled, respond to <fieldset disabled>.
defineOptions(formAssociatedHost());

const props = defineProps(rangeProps);

defineEmits(["input", "change"]);
const { emitBubbling } = useHostEmit();

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, not a standalone toBoolean(prop).
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const showTicks = computed(() => toBoolean(props.showTicks));

// The `value` attribute seeds the slider and names the value a reset restores; the `value`
// property is the live position. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value ?? props.min);
const model = ref(defaultValue.value);

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value ?? props.min;
    model.value = defaultValue.value;
  },
);

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => model.value,
    set: (value) => (model.value = value),
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

// The inner range input sits in the shadow root, where the outer form cannot see it, so the
// element submits through ElementInternals. `change` is already reported on commit below.
const { mirrorFormValue, onFormReset } = useForm();
const formValue = computed(() => String(model.value ?? ""));

mirrorFormValue(formValue);

onFormReset(() => (model.value = defaultValue.value));

// The only path that emits `input`, matching a native control, which reports user input alone.
function onInput(event) {
  model.value = Number(event.target.value);
  emitBubbling("input", model.value);
}

// The native range-input change fires when the thumb is released (or a keyboard
// step completes) — the committed value, as opposed to input's per-tick stream.
function onChange(event) {
  emitBubbling("change", Number(event.target.value));
}

const fillWidth = computed(() => {
  const range = props.max - props.min;

  if (range === 0) {
    return 0;
  }

  const value = model.value - props.min;

  // Clamp the percentage value between 0% and 100%,
  // in case `value` is below 0 or exceeds the defined `range`.
  const computedWidth = Math.max(0, Math.min((value / range) * 100, 100));
  // Return the width as a CSS style string
  return `width: ${computedWidth}%;`;
});

const tickCount = computed(() => Math.floor((props.max - props.min) / props.step) + 1);
</script>

<style>
:host {
  --track-size: var(--sp-input-range-slider-track-size, 6px);
  --track-color: var(--sp-input-range-slider-track-color, #ccc);
  --track-border-radius: var(--sp-input-range-slider-track-border-radius, calc(var(--track-size) / 2));
  --track-fill-color: var(--sp-input-range-slider-track-fill-color, var(--sp-sys-color-primary, #1976d2));
  --track-hover-cursor: var(--sp-input-range-slider-track-hover-cursor, pointer);
  --thumb-size: var(--sp-input-range-slider-thumb-size, 16px);
  --thumb-color: var(--sp-input-range-slider-thumb-color, var(--sp-sys-color-primary, #1976d2));
  --thumb-border-color: var(--sp-input-range-slider-thumb-border-color, transparent);
  --thumb-border-width: var(--sp-input-range-slider-thumb-border-width, 0px);
  --thumb-border-radius: var(--sp-input-range-slider-thumb-border-radius, 50%);
  --hit-size: var(--sp-input-range-slider-hit-size, 24px);
  /* Clamped so a hit-size smaller than the track can't push the input below the drawn track. */
  --hit-overhang: max(0px, calc((var(--hit-size) - var(--track-size)) / 2));

  display: block;
}

/* Disabled styling driven by the native :disabled state (own attribute or an
   ancestor <fieldset disabled>), no JS required. */
:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}
</style>

<style scoped lang="scss">
.sp-input-range-slider {
  display: flex;
  align-items: center;
  gap: 8px;

  &__track-container {
    position: relative;
    width: 100%;
    height: var(--track-size);
  }

  &__track-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--track-color);
    border-radius: var(--track-border-radius);
    z-index: 0;
  }

  &__track-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background-color: var(--track-fill-color);
    border-radius: var(--track-border-radius) 0 0 var(--track-border-radius);
    z-index: 1;

    // Make sure the fill width is dynamically calculated from the thumb’s center to avoid layout gaps or visual overlaps
    min-width: calc(var(--thumb-size) / 2);
    max-width: calc(100% - var(--thumb-size) / 2);
  }

  &__input {
    display: block;
    appearance: none;
    width: 100%;
    height: var(--track-size);
    // Transparent vertical borders on a content-box enlarge the click/touch
    // target to --hit-size without moving the content box, so the browser's
    // internal track (and the thumb riding it) stays aligned with the drawn
    // track divs behind it.
    box-sizing: content-box;
    border: solid transparent;
    border-width: var(--hit-overhang) 0;
    background-color: transparent;
    // iOS flashes a grey tap highlight across the input's whole (enlarged) box on touch.
    -webkit-tap-highlight-color: transparent;
    outline: none;
    position: absolute;
    top: calc(-1 * var(--hit-overhang));
    z-index: 3;
    padding: 0;
    margin: 0;

    &:hover {
      cursor: var(--track-hover-cursor);
    }

    &::-webkit-slider-runnable-track {
      background: transparent;
      height: var(--track-size);
    }

    &::-webkit-slider-thumb {
      appearance: none;
      width: var(--thumb-size);
      height: var(--thumb-size);
      background-color: var(--thumb-color);
      border-radius: 50%;
      cursor: pointer;
      border: none;
      // iOS WebKit's UA stylesheet puts a box-shadow on the thumb; appearance: none doesn't clear it.
      box-shadow: none;
      margin-top: calc((var(--thumb-size) - var(--track-size)) / -2);
    }

    &::-moz-range-track {
      background: transparent;
      height: var(--track-size);
    }

    &::-moz-range-thumb {
      width: var(--thumb-size);
      height: var(--thumb-size);
      background-color: var(--thumb-color);
      border-radius: 50%;
      cursor: pointer;
      border: none;
    }
  }

  &__ticks {
    position: absolute;
    top: 1px;
    left: calc(var(--thumb-size) / 2);
    width: calc(100% - var(--thumb-size));
    height: 0;
    display: flex;
    justify-content: space-between;
    pointer-events: none;
    z-index: 3;
  }

  &__tick {
    position: relative;
    width: 1px;
    background-color: var(--sp-input-range-slider-tick-color, #fff);
    height: 3px;
  }
}
</style>
